import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import NotificationBell from "../components/NotificationBell";
import { useCompany } from "../context/CompanyContext";
import { isModuleOn as moduleIsOn } from "../utils/modules";
import { BILL_ENTRY_ROLES, canDoAccounts } from "../services/purchaseBillService";

// ICONS
import {
  Home,
  ClipboardList,  PlusSquare,
  Wrench,
  CreditCard,
  BarChart3,
  ShoppingCart,
  CheckCircle,
  Building2,
  LayoutDashboard,
  UserCog,
  Package,
  FileText,
  Truck,
  Hammer
} from "lucide-react";

function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [openSections, setOpenSections] = useState(() => {
    const open = new Set(["Dashboard"]);
    return open;
  });
  const { logout } = useAuth();

  let user = {};

  try {
    const raw = localStorage.getItem("user");
    user = raw ? JSON.parse(raw) : {};
  } catch (err) {
    console.error("User parse error:", err);
    user = {};
  }

  const role = user?.role || "technician";
const { company } = useCompany();
const isModuleOn = (mod) => moduleIsOn(company?.modules, mod);

  // ── OEM Returns pending-count badge ──────────────────────────────────
  const [oemPendingCount, setOemPendingCount] = useState(0);

  useEffect(() => {
    const fetchOemPendingCount = async () => {
      if (!user?.company_id) return;
      if (!["sm", "manager", "owner"].includes(role)) return; // only roles that see OEM Returns
      try {
        const snap = await getDocs(query(
          collection(db, "service_tasks"),
          where("company_id", "==", user.company_id),
        ));
        let count = 0;
        snap.docs.forEach(d => {
          const items = d.data().defective_items || [];
          count += items.filter(item => !item.oem_return_status || item.oem_return_status === "pending").length;
        });
        setOemPendingCount(count);
      } catch (err) {
        console.warn("OEM pending count fetch failed (non-blocking):", err.message);
      }
    };
    fetchOemPendingCount();
  }, [user?.company_id, role]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = async () => {
    await logout();
  };
  const userName = user?.name || "User";

  const initials = userName
    .split(" ")
    .map(word => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const navItems = [
  { name: "Home", path: "/", icon: Home },
  { name: "Enquiries", path: "/enquiries", roles: ["owner", "sm", "manager"], icon: ClipboardList, module: "service" },
  { name: "Client Master", path: "/master/client", roles: ["owner", "sm", "manager"], icon: Building2, module: "projects" },
  { name: "Client List", path: "/master/client-list", roles: ["owner", "sm", "manager"], icon: LayoutDashboard, module: "projects" },
  { name: "Project List", path: "/projects", roles: ["owner", "sm", "manager"], icon: ClipboardList, module: "projects" },
  { name: "My Projects",  path: "/projects", roles: ["technician", "site_engineer"], strictRoles: true, icon: ClipboardList, module: "projects" },
  { name: "Quotations", path: "/quotations", roles: ["owner", "sm", "manager"], icon: FileText, module: "projects" },
  { name: "Create Material Request", path: "/mrf", isPrimary: true, roles: ["technician", "sm", "owner", "manager", "store", "site_engineer"], icon: PlusSquare, module: "inventory" },
  { name: "Purchase Requests", path: "/purchase-list", roles: ["sm", "owner", "manager", "store"], icon: ShoppingCart, module: "procurement" },
  { name: "Purchase Orders", path: "/purchase-orders", roles: ["sm", "owner", "manager", "store"], icon: FileText, module: "procurement" },
  { name: "Awaiting Bills", path: "/awaiting-bills", roles: BILL_ENTRY_ROLES, icon: FileText, module: "procurement" },
  { name: "Confirm Site Receipt", path: "/my-site-confirmations", roles: ["site_engineer"], strictRoles: true, icon: FileText },
  { name: "Material Summary", path: "/store-summary", roles: ["sm", "owner", "manager", "store"], icon: Package, module: "inventory" },
  { name: "Procurement Queue", path: "/store-procurement", roles: ["sm", "owner", "manager", "store"], icon: ShoppingCart, module: "procurement" },
  { name: "Item Master",   path: "/item-master",   roles: ["sm", "owner", "manager", "store"], icon: Package,       module: "inventory" },
  { name: "Item Requests", path: "/item-requests", roles: ["sm", "owner", "manager", "store"], icon: ClipboardList, module: "inventory" },
  { name: "Spare Part Requests", path: "/spare-part-requests", roles: ["sm", "owner", "manager", "store"], icon: Wrench, module: "inventory" },
  { name: "Equipment Registry", path: "/equipment-registry", roles: ["sm", "owner", "manager", "store"], icon: Hammer, module: "assets" },
  { name: "Site Stock", path: "/site-stock", roles: ["sm", "owner", "manager", "store"], icon: Package, module: "inventory" },
  { name: "Site Transfers", path: "/site-transfers-pending", roles: ["sm", "owner", "manager", "store"], icon: Truck, module: "inventory" },
  { name: "MRF Issue", path: "/store-mrf", roles: ["sm", "owner", "manager", "store"], icon: ClipboardList, module: "inventory" },
  { name: "Direct Issue", path: "/direct-issue", roles: ["sm", "owner", "manager", "store"], icon: ShoppingCart, module: "inventory" },
  { name: "Return Material", path: "/inventory-return", roles: ["sm", "owner", "manager", "store"], icon: ShoppingCart, module: "inventory" },
  { name: "Defective Return", path: "/defective-return", roles: ["sm", "owner", "manager", "store"], icon: BarChart3, module: "inventory" },
  { name: "Stock Adjustment", path: "/stock-adjustment", roles: ["sm", "owner", "manager", "store"], icon: PlusSquare, module: "inventory" },
  { name: "Stock Adjustment Approval", path: "/stock-adjustment-approval", roles: ["sm", "owner", "manager", "store"], icon: CheckCircle, module: "inventory" },
  { name: "Vendor Master", path: "/vendor-master", roles: ["sm", "owner", "manager", "store"], icon: Truck, module: "procurement" },
  { name: "Vendor List", path: "/vendor-list", roles: ["sm", "owner", "manager", "store"], icon: Truck, module: "procurement" },
  { name: "Vendor Return", path: "/vendor-return", roles: ["sm", "owner", "manager", "store"], icon: ShoppingCart, module: "procurement" },
  { name: "Inventory Ledger", path: "/inventory-ledger", roles: ["sm", "owner", "manager", "store"], icon: ClipboardList, module: "inventory" },
  { name: "Defective Stock", path: "/defective-stock", roles: ["sm", "owner", "manager", "store"], icon: BarChart3, module: "inventory" },
  { name: "Expenses", path: "/expenses", roles: ["technician", "sm", "owner", "manager", "store", "site_engineer"], icon: CreditCard, module: "expenses" },
  { name: "Accounts Dashboard", path: "/accounts", roles: ["owner", "accounts"], accountsAccess: true, icon: CreditCard, module: "accounts" },
  { name: "Payment Entry", path: "/payment-entry", roles: ["owner", "accounts"], icon: CreditCard, module: "accounts" },
  { name: "Expense Ledger", path: "/expense-ledger", roles: ["owner", "accounts"], icon: BarChart3, module: "accounts" },
  { name: "Income Ledger", path: "/income-ledger", roles: ["owner", "accounts"], icon: BarChart3, module: "accounts" },
  { name: "Cash / Bank Book", path: "/cash-bank-book", roles: ["owner", "accounts"], accountsAccess: true, icon: CreditCard, module: "accounts" },
  // "Vendor Payments" retired (D-289) — vendors are paid on Payables (khaata)
    { name: "Payables", path: "/payables", roles: ["owner", "accounts"], accountsAccess: true, icon: CreditCard, module: "accounts" },
    { name: "Opening Balances", path: "/opening-balances", roles: ["owner", "accounts"], accountsAccess: true, icon: CreditCard, module: "accounts" },
  { name: "Billing Dashboard", path: "/billing/dashboard", roles: ["owner", "accounts"], icon: FileText, module: "billing" },
  { name: "Create Invoice", path: "/billing/create", roles: ["owner", "accounts"], icon: PlusSquare, module: "billing" },
  { name: "Invoice List", path: "/billing/invoices", roles: ["owner", "accounts"], icon: ClipboardList, module: "billing" },
  { name: "Reports Dashboard", path: "/reports/dashboard", roles: ["owner", "accounts"], icon: BarChart3, module: "reports" },
  { name: "Financial Reports", path: "/reports/financial", roles: ["owner", "accounts"], icon: CreditCard, module: "reports" },
  { name: "Stock List", path: "/stock-list", roles: ["sm", "owner", "manager", "store"], icon: Package, module: "inventory" },
  { name: "Inventory Dashboard", path: "/inventory-dashboard", roles: ["sm", "owner", "manager", "store"], icon: BarChart3, module: "inventory" },
  { name: "Inventory Reports", path: "/reports/inventory", roles: ["owner"], icon: ClipboardList, module: "reports" },
  { name: "Create Asset", path: "/assets", roles: ["sm", "owner", "manager", "store"], icon: Hammer, module: "assets" },
  { name: "Transfer Asset", path: "/assets/transfer", roles: ["sm", "owner", "manager", "technician", "store", "site_engineer"], icon: Truck, module: "assets" },
  { name: "Pending Transfers", path: "/assets/pending", roles: ["sm", "owner", "manager", "technician", "store", "site_engineer"], icon: ClipboardList, module: "assets" },
  { name: "Asset Ledger", path: "/assets/ledger", roles: ["sm", "owner", "manager", "store"], icon: FileText, module: "assets" },
  { name: "Return Assets", path: "/assets/returns", roles: ["sm", "owner", "manager", "technician", "store", "site_engineer"], icon: Truck, module: "assets" },
  { name: "Receive Returns", path: "/assets/receive-returns", roles: ["sm", "owner", "manager", "store"], icon: ClipboardList, module: "assets" },
  { name: "Project Reports", path: "/reports/projects", roles: ["owner"], icon: LayoutDashboard, module: "reports" },
  { name: "Expense Approval", path: "/expense-approval", roles: ["sm", "owner", "manager", "store"], icon: CheckCircle, module: "expenses" },
  { name: "Transport", path: "/transport", roles: ["sm", "owner", "manager", "store"], icon: Truck, module: "transport" },
  { name: "Subcontractor Work", path: "/subcontractor-work", roles: ["sm", "owner", "manager", "site_engineer", "technician"], icon: Hammer },
  { name: "Service List", path: "/services", roles: ["sm", "owner", "technician", "manager"], icon: Wrench, module: "service" },
  { name: "Service Dashboard", path: "/service-dashboard", roles: ["sm", "owner", "manager"], icon: LayoutDashboard, module: "service" },
  { name: "AMC Contracts", path: "/amc-contracts", roles: ["sm", "manager", "owner"], icon: Wrench, module: "amc" },
  { name: "OEM Returns", path: "/oem-returns", roles: ["sm", "manager", "owner"], icon: Package, module: "amc" },
  { name: "Site Machines", path: "/machines", roles: ["sm", "manager", "owner", "technician", "site_engineer"], icon: Hammer, module: "service" },
  { name: "Technician Dashboard", path: "/technician", roles: ["technician"], icon: UserCog, module: "service" },
  { name: "Company Profile", path: "/company-profile", roles: ["owner"], icon: Building2 },
  { name: "User Management", path: "/users", roles: ["owner"], icon: UserCog },
  { name: "Team Activity", path: "/team-activity", roles: ["owner"], icon: LayoutDashboard },
  { name: "Tally Export", path: "/tally-export", roles: ["owner", "accounts"], icon: FileText, module: "accounts" },
];

  const filteredNav = navItems.filter((item) => {
  if (item.roles) {
    if (item.strictRoles) {
      if (!item.roles.includes(role)) return false;
    } else if (role !== "owner" && !item.roles.includes(role) && !(item.accountsAccess && canDoAccounts(user))) {
      return false;
    }
  }
  if (item.module && !isModuleOn(item.module)) return false;
  return true;
});

  const renderMenu = () => {
    const groupedSections = {
      Dashboard:        ["Home", "Team Activity"],
      CRM:              ["Enquiries", "Client Master", "Client List"],
      Projects:         ["Project List", "Quotations"],
      Service:      ["Service Dashboard", "Service List", "AMC Contracts", "OEM Returns", "Site Machines", "My Projects"],
      Store:            ["Material Summary", "Procurement Queue", "Item Master", "Item Requests"],
      Purchase:         ["Vendor Master", "Vendor List", "Purchase Requests", "Purchase Orders", "Awaiting Bills", "Confirm Site Receipt", "Vendor Return"],
      Inventory:        ["Equipment Registry", "Site Stock", "Site Transfers", "Stock List", "Inventory Dashboard", "Inventory Ledger", "Stock Adjustment", "Stock Adjustment Approval", "Defective Stock"],
      "Tools & Assets": ["Create Asset", "Transfer Asset", "Pending Transfers", "Asset Ledger", "Return Assets", "Receive Returns"],
      "Issue / Return": ["MRF Issue", "Direct Issue", "Return Material", "Defective Return", "Spare Part Requests"],
      "Material Request": ["Create Material Request"],
      Expenses:         ["Expenses", "Expense Approval"],
      Transport:        ["Transport"],
      Subcontractors:   ["Subcontractor Work"],
      Accounts:         ["Accounts Dashboard", "Payables", "Opening Balances", "Payment Entry", "Expense Ledger", "Income Ledger", "Cash / Bank Book", "Tally Export"],
      Billing:          ["Billing Dashboard", "Create Invoice", "Invoice List"],
      Reporting:        ["Reports Dashboard", "Financial Reports", "Inventory Reports", "Project Reports"],
      Users:            ["Company Profile", "User Management"],
    };

    const toggleSection = (section) => {
      setOpenSections(prev => {
        const next = new Set(prev);
        next.has(section) ? next.delete(section) : next.add(section);
        return next;
      });
    };

    const exactMatchPaths = ["/assets", "/purchase-list", "/purchase-orders", "/mrf", "/expenses", "/master/client", "/master/client-list", "/enquiries", "/stock-adjustment"];

    return (
      <div className="flex flex-col gap-1 p-3">
        {Object.entries(groupedSections).map(([section, itemNames]) => {
          // Order follows groupedSections' array order (declared above),
          // NOT navItems' declaration order — .filter() alone preserves
          // the source array's sequence, which silently ignored any
          // reordering done in groupedSections. .map()+find() here makes
          // groupedSections the actual source of truth for display order.
          const sectionItems = itemNames
            .map(name => filteredNav.find(item => item.name === name))
            .filter(Boolean);
          if (!sectionItems.length) return null;

          const isExpanded = openSections.has(section);

          // Check if any item in section is active
          const hasActive = sectionItems.some(item => {
            if (item.path === "/") return location.pathname === "/";
            if (exactMatchPaths.includes(item.path)) return location.pathname === item.path;
            return location.pathname.startsWith(item.path);
          });

          return (
            <div key={section} className="mb-1">
              {/* Section heading — clickable */}
              <button
                onClick={() => toggleSection(section)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-xl transition-all ${
                  hasActive
                    ? "text-cyan-400 bg-white/5"
                    : "text-gray-400 hover:text-cyan-300 hover:bg-white/5"
                }`}
              >
                <span className="text-xs font-bold uppercase tracking-wider">
                  {section}
                </span>
                <span className={`text-xs transition-transform duration-200 ${isExpanded ? "rotate-90" : ""}`}>
                  ›
                </span>
              </button>

              {/* Section items — collapsible */}
              {isExpanded && (
                <div className="flex flex-col gap-1 mt-1 ml-2">
                  {sectionItems.map((item, index) => {
                    const isActive = (() => {
                      if (item.path === "/") return location.pathname === "/";
                      if (exactMatchPaths.includes(item.path)) return location.pathname === item.path;
                      return location.pathname.startsWith(item.path);
                    })();

                    const Icon = item.icon;

                    if (item.isPrimary) {
                      return (
                        <button key={index}
                          onClick={() => { navigate(item.path); setIsOpen(false); }}
                          className="bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 shadow-lg shadow-blue-500/20 text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:opacity-90 transition my-1">
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-gradient-to-tr from-cyan-400/20 to-teal-500/20 border border-white/10">
                              <Icon size={14} className="text-cyan-300" />
                            </div>
                            {item.name}
                          </div>
                        </button>
                      );
                    }

                    return (
                      <button key={index}
                        onClick={() => { navigate(item.path); setIsOpen(false); }}
                        className={`text-left px-3 py-2 rounded-lg text-sm transition-all ${
                          isActive
                            ? "bg-gradient-to-r from-cyan-500/20 to-teal-500/20 text-cyan-300 font-semibold border-l-2 border-cyan-400"
                            : "text-gray-400 hover:text-gray-200 hover:bg-white/8 hover:translate-x-1"
                        }`}>
                        <div className="flex items-center gap-2.5 justify-between w-full">
                          <div className="flex items-center gap-2.5">
                            <Icon size={15} className={`min-w-[15px] ${isActive ? "text-cyan-400" : "text-gray-500"}`} />
                            <span className="whitespace-nowrap text-left text-[13px]">{item.name}</span>
                          </div>
                          {item.name === "OEM Returns" && oemPendingCount > 0 && (
                            <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                              {oemPendingCount}
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="flex h-screen w-full max-w-full overflow-hidden bg-gradient-to-br from-slate-100 via-gray-100 to-slate-200">

      {/* DESKTOP SIDEBAR */}
      <div className="hidden md:flex flex-col w-64 h-screen overflow-y-auto bg-gradient-to-b from-[#0f172a] via-[#0b2a2f] to-[#042f2e] border-r border-white/10 shadow-xl text-white">
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3 px-1">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gradient-to-tr from-cyan-400 to-teal-500 shadow-md">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M5 6H19L7 18H19" stroke="black" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="leading-tight">
              <div className="text-base font-semibold tracking-wide text-white">ZENO</div>
              <div className="text-[11px] text-gray-400">Field Service</div>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">{renderMenu()}</div>
      </div>

      {/* MOBILE DRAWER */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black opacity-40" onClick={() => setIsOpen(false)} />
          <div className="relative w-64 max-h-screen overflow-y-auto bg-gradient-to-b from-[#0B1F2A] via-[#0F2A36] to-[#0A1A22] text-white h-full shadow-lg z-50">
            <div className="p-4 font-bold text-lg border-b border-gray-700">ZENO</div>
            <div className="flex-1 overflow-y-auto">{renderMenu()}</div>
          </div>
        </div>
      )}

      {/* MAIN CONTENT */}
      <div className="flex flex-col flex-1 min-w-0 w-full overflow-x-hidden">

        {/* TOP HEADER */}
        <div className="flex flex-wrap md:flex-nowrap items-center justify-between gap-3 bg-white/80 backdrop-blur-md border-b border-gray-200 px-4 py-3 shadow-sm">

          <button className="md:hidden text-xl" onClick={() => setIsOpen(true)}>☰</button>

          {/* PAGE TITLE */}
          <div className="font-semibold">
            {(() => {
              if (location.pathname.startsWith("/purchase-orders")) return "Purchase Orders";
              if (location.pathname.startsWith("/purchase-list"))   return "Purchase Requests";
              if (location.pathname.startsWith("/purchase-bills"))  return "Purchase Bill";
              return (
                [...filteredNav]
                  .sort((a, b) => b.path.length - a.path.length)
                  .find(i =>
                    i.path === "/"
                      ? location.pathname === "/"
                      : location.pathname.startsWith(i.path)
                  )?.name || "Dashboard"
              );
            })()}
          </div>

          {/* ── RIGHT SIDE: Bell + Logout + User ── */}
          <div className="flex items-center gap-3 ml-auto">

            {/* ✅ NOTIFICATION BELL — placed before logout */}
            <NotificationBell />

            <button
              onClick={handleLogout}
              className="px-3 py-2 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600">
              Logout
            </button>

            <span className="hidden sm:block text-sm text-gray-500 whitespace-nowrap">
              Welcome, {userName}
            </span>

            <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-sm font-semibold">
              {initials}
            </div>

          </div>
        </div>

        {/* PAGE CONTENT */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-2 md:p-4 w-full max-w-full">
          <Outlet />
        </div>

      </div>
    </div>
  );
}

export default MainLayout;
